import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import Icon from '../components/ui/Icon';
import { usePopup } from '../context/PopupContext';
import { clearAuth, getUser } from '../utils/authUtil';
import ThemeSetting from '../components/ui/ThemeSetting';
import './StudentSettings.css';

export const REPLAY_TOUR_KEY = 'zaptude_replay_tour';

/**
 * Settings: account details (read-only), change password (POST /auth/change-password), replay the welcome tour, sign out.
 * Deliberately small — identity lives in Profile, this is only the account.
 */
export default function StudentSettings() {
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const user = getUser();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); if (problem) setProblem(''); };

  const ready = form.current && form.next.length >= 8 && form.next === form.confirm;
  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;

  const changePassword = async (e) => {
    e.preventDefault();
    if (!ready || saving) return;
    setSaving(true); setProblem('');
    try {
      await API.post('/auth/change-password', { current_password: form.current, new_password: form.next });
      setForm({ current: '', next: '', confirm: '' });
      showToast('Password updated.', { tone: 'success' });
    } catch (err) {
      setProblem(err.response?.data?.message || (err.response ? 'Could not update your password.' : 'You appear to be offline. Try again when you\'re connected.'));
    } finally { setSaving(false); }
  };

  const replayTour = () => { try { sessionStorage.setItem(REPLAY_TOUR_KEY, '1'); } catch { /* ignore */ } navigate('/student/dashboard'); };
  const logout = async () => { if (await showConfirm('Log out', 'Are you sure you want to log out of Zaptude?')) { clearAuth(); navigate('/login'); } };

  return (
    <div className="z-page z-page--narrow settings-page">
      <section className="z-section" aria-labelledby="set-account">
        <h2 className="z-section-title" id="set-account">Account</h2>
        <div className="z-list">
          <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">Name</span><span className="z-list-row-title">{user?.name}</span></span></div>
          <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">Email</span><span className="z-list-row-title settings-wrap">{user?.email}</span></span></div>
          {user?.collegeName && <div className="z-list-row settings-static"><span className="z-list-row-body"><span className="z-list-row-sub">College</span><span className="z-list-row-title">{user.collegeName}</span></span></div>}
        </div>
      </section>

      <ThemeSetting />

      <section className="z-section" aria-labelledby="set-password">
        <h2 className="z-section-title" id="set-password">Change password</h2>
        <form className="settings-form z-surface" onSubmit={changePassword} noValidate>
          <div className="z-field">
            <label className="z-label" htmlFor="set-current">Current password</label>
            <input id="set-current" className="z-input" type="password" autoComplete="current-password" value={form.current} onChange={set('current')} />
          </div>
          <div className="z-field">
            <label className="z-label" htmlFor="set-next">New password</label>
            <input id="set-next" className="z-input" type="password" autoComplete="new-password" value={form.next} onChange={set('next')} aria-describedby="set-next-help" />
            <span className="z-help" id="set-next-help">At least 8 characters, with a letter and a number.</span>
          </div>
          <div className="z-field">
            <label className="z-label" htmlFor="set-confirm">Confirm new password</label>
            <input id="set-confirm" className="z-input" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} aria-invalid={mismatch} />
            {mismatch && <span className="z-error-text" role="alert">The passwords don't match.</span>}
          </div>
          {problem && <div className="z-error-text" role="alert">{problem}</div>}
          <button type="submit" className="z-btn z-btn--primary z-btn--block" disabled={!ready || saving}>
            <Icon name="key" size={18} /> {saving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>

      <section className="z-section" aria-labelledby="set-more">
        <h2 className="z-section-title" id="set-more">More</h2>
        <div className="z-list">
          <button type="button" className="z-list-row" onClick={replayTour}>
            <span className="z-list-row-body"><span className="z-list-row-title">Replay the welcome tour</span><span className="z-list-row-sub" style={{ display: 'block' }}>A quick look around Home</span></span>
            <Icon name="chevron-right" size={18} />
          </button>
          <button type="button" className="z-list-row z-list-row--danger" onClick={logout}>
            <span className="z-list-row-body"><span className="z-list-row-title">Log out</span></span>
            <Icon name="logout" size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
